import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import type { CARD_TYPE } from 'modules/expenses/lib/types'
import { useSubscriptions as useExpensesSubscriptions } from 'modules/expenses/store/collections/subscriptions'

export interface IPhysicalCardsAvailability {
  available: boolean
  warningMessage?: string
  usageLimit?: number
  unitPrice?: string
}

export const useCardsCreation = () => {
  const expensesSubscriptions = useExpensesSubscriptions()

  const canCreateCard = (cardType: CARD_TYPE) => {
    if (hasFeature('dev_packaging_paywalls')) {
      return true
    }
    if (hasFeature('dev_expenses_v2_addon')) {
      return expensesSubscriptions.current.canCreateCard(cardType)
    }

    return true
  }
  return { canCreateCard }
}

export const usePhysicalCardsCreationLimits = (featureAvailability: FeatureAvailabilityType) => {
  const {
    access: { locale: accessLocale },
    company: { currency },
  } = useCurrentSession()

  const physicalCardsAvailability = ((): IPhysicalCardsAvailability => {
    if (!hasFeature('dev_packaging_paywalls')) {
      return {
        available: true,
      }
    }

    const defaultAvailability = {
      available: false,
    }

    const available = featureAvailability.available

    if (!('usageLimit' in featureAvailability) && !available) return defaultAvailability

    const usage = featureAvailability.usage || 0

    const usageLimit = featureAvailability.usageLimit || -1

    const limitExceededIn = Number(usageLimit) === -1 ? -1 : Math.max(usage - usageLimit, 0)
    const unitPrice = Number(featureAvailability.additionalUnitPrice) || 0
    const currencyCode = featureAvailability.currencyCode || currency || 'EUR'

    const amount = MoneyWithCurrency({
      amount: unitPrice * 100,
      currency: isCurrency(currencyCode) ? currencyCode : 'EUR',
      locale: accessLocale,
    })

    const warningMessage = i18n.t('expenses_v2.banners.physical_cards_for_free_limit', {
      limit: usageLimit,
      extra_usage: limitExceededIn,
      amount,
    })

    return {
      available: available,
      warningMessage: !available && usageLimit > -1 ? warningMessage : undefined,
      usageLimit: usageLimit,
      unitPrice: amount,
    }
  })()

  return {
    physicalCardsAvailability,
  }
}
